import React, { useEffect, useMemo, useState } from 'react';
import { FaFileDownload, FaFilePdf, FaSave, FaSyncAlt, FaExclamationTriangle, FaTimesCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import declaracionesJuradasService from '../../services/declaracionesJuradasService';
import CampoMonto from '../InversionesExterior/CampoMonto';
import { conMiles } from '../InversionesExterior/formatos';
import {
    LINEAS_INGRESOS, LINEAS_EGRESOS, CREDITOS_IDPC, CAMPOS_BASE, CAMPOS_LINEAS, CASTIGO_SUJETO_RESTITUCION,
    valorLinea, totales, reparto, totalesDeclaracion, revisar
} from '../../services/dj1947/calculo';
import '../../styles/components/DJ1947TransparenciaTab.css';

const CAMPOS = [...CAMPOS_LINEAS, ...CAMPOS_BASE];
const pesos = (monto) => `$${conMiles(Math.round(Number(monto) || 0))}`;

// Lo guardado, como lo edita el formulario (montos en dígitos, porcentaje con coma)
const formularioDesde = (datos) => ({
    base: Object.fromEntries(CAMPOS.map((campo) => [campo, String(datos.base[campo] ?? 0)])),
    propietarios: datos.propietarios.map((p) => ({
        id: p.id,
        rut: p.rut,
        nombre: p.nombre,
        tipo_persona: p.tipo_persona,
        porcentaje: String(Number(p.porcentaje)).replace('.', ','),
        retiros: String(p.retiros ?? 0),
    })),
});

const aNumeroPorcentaje = (texto) => Number(String(texto).replace(',', '.')) || 0;

// Notas de las líneas que TAXI propone, para saber de dónde sale cada monto
const notaDeLinea = (codigo, propuesta) => {
    switch (codigo) {
        case '1600': return 'Ventas, boletas y comprobantes del año, menos notas de crédito. Descuenta lo no percibido.';
        case '1607': return 'TAXI propone el reajuste de los PPM (Circular SII N° 62 de 2020). Suma a mano otros ingresos, como el reajuste del remanente de IVA.';
        case '1605': return 'Suma de los créditos por IDPC de las participaciones en otras empresas (sección de créditos).';
        case '1614': return 'Compras netas del año, menos notas de crédito.';
        case '1616': return propuesta.remuneraciones_brutas !== undefined
            ? `Remuneraciones brutas ${pesos(propuesta.remuneraciones_brutas)} menos cotizaciones de diciembre adeudadas al 31-12 ${pesos(propuesta.remuneraciones_adeudadas)}.`
            : null;
        case '1617': return 'Honorarios brutos del año.';
        case '1620': return 'Arriendos pagados en el año.';
        case '1625': return 'Gastos generales de las compras y patentes pagadas.';
        case '1627': return propuesta.cod_1627 !== undefined
            ? 'Pérdida del año tributario anterior según su certificado de renta pagada (código 1713), a valor nominal (Circular SII N° 62 de 2020, apartado 2.4.1).'
            : 'Pérdida del año tributario anterior, a valor nominal (Circular SII N° 62 de 2020, apartado 2.4.1). Sin certificado de renta pagada de ese año en TAXI: ingrésala a mano.';
        default: return null;
    }
};

/**
 * Componente para DJ1947 - Base imponible, créditos y PPM de los propietarios de una
 * empresa Pro Pyme Transparente (14 D N°8)
 *
 * Al crear la declaración, TAXI propone la base imponible del año (recuadro 22 del F22),
 * los PPM y un propietario por socio. Aquí se revisa y corrige, se guarda y se descarga el
 * CSV para el importador del SII. La renta 14 D N°8 usará lo que quede guardado.
 * El reparto y la revisión salen de services/dj1947/calculo.js, el mismo que usa el servidor.
 */
const DJ1947TransparenciaTab = ({ declaracion, year, idCompany, rutEmpresa, token }) => {
    const [datos, setDatos] = useState(null);
    const [formulario, setFormulario] = useState(null);
    const [ocupado, setOcupado] = useState(false);

    const aplicar = (respuesta) => {
        setDatos(respuesta);
        setFormulario(formularioDesde(respuesta));
    };

    useEffect(() => {
        const cargar = async () => {
            setDatos(null);
            try {
                aplicar(await declaracionesJuradasService.getDJ1947(declaracion.id, idCompany, token));
            } catch (error) {
                Swal.fire({ icon: 'error', title: 'Error', text: error.message });
            }
        };
        cargar();
    }, [declaracion.id, idCompany, token]);

    // Totales, reparto y revisión de lo que está en pantalla, con el cálculo del servidor
    const vista = useMemo(() => {
        if (!formulario) return null;
        const base = formulario.base;
        const propietarios = formulario.propietarios.map((p) => ({ ...p, porcentaje: aNumeroPorcentaje(p.porcentaje) }));
        const filas = reparto(base, propietarios);
        return { totales: totales(base), filas, totalesDj: totalesDeclaracion(filas), ...revisar(base, propietarios) };
    }, [formulario]);

    if (!datos || !formulario || !vista) {
        return <div className="dj1947-loading">Cargando datos...</div>;
    }

    const hayCambios = JSON.stringify(formulario) !== JSON.stringify(formularioDesde(datos));
    const propuesta = datos.base.propuesta || {};
    const anioComercial = datos.base.anio_comercial;

    const cambiarBase = (campo, valor) => setFormulario((f) => ({ ...f, base: { ...f.base, [campo]: valor } }));
    const cambiarPropietario = (id, campo, valor) => setFormulario((f) => ({
        ...f,
        propietarios: f.propietarios.map((p) => (p.id === id ? { ...p, [campo]: valor } : p)),
    }));

    const handleGuardar = async () => {
        setOcupado(true);
        try {
            aplicar(await declaracionesJuradasService.guardarDJ1947(declaracion.id, idCompany, {
                base: formulario.base,
                propietarios: formulario.propietarios.map(({ id, porcentaje, retiros }) => ({ id, porcentaje, retiros })),
            }, token));
            Swal.fire({ icon: 'success', title: 'Guardado', text: 'La DJ 1947 quedó guardada.', timer: 1800, showConfirmButton: false });
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'No se guardó', text: error.message });
        } finally {
            setOcupado(false);
        }
    };

    const handleRegenerar = async () => {
        const confirmacion = await Swal.fire({
            icon: 'question',
            title: 'Volver a traer de TAXI',
            html: 'Se vuelven a poner los montos que propone TAXI en las líneas que vienen de sus datos (1600, 1607, 1614, 1616, 1617, 1620 y 1625; el 1627 si hay certificado de renta pagada del año anterior) y en los PPM, y se actualizan los socios y sus retiros. Se mantienen las demás líneas, los créditos y el porcentaje de cada socio. Los certificados se vuelven a numerar desde 1.',
            showCancelButton: true,
            confirmButtonText: 'Volver a traer',
            cancelButtonText: 'Cancelar',
        });
        if (!confirmacion.isConfirmed) return;
        setOcupado(true);
        try {
            aplicar(await declaracionesJuradasService.regenerarDJ1947(declaracion.id, idCompany, token));
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: error.message });
        } finally {
            setOcupado(false);
        }
    };

    const handleDescargar = async () => {
        setOcupado(true);
        try {
            await declaracionesJuradasService.downloadAndSaveDJ1947CSV(declaracion.id, idCompany, rutEmpresa, year, token);
            Swal.fire({ icon: 'success', title: 'Descarga exitosa', text: `Súbelo en el importador del SII, formulario 1947 del AT ${year}.` });
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'No se generó el CSV', text: error.message });
        } finally {
            setOcupado(false);
        }
    };

    // Certificado N° 69 de todos los propietarios, o solo del RUT indicado
    const handleCertificados = async (rut) => {
        setOcupado(true);
        try {
            await declaracionesJuradasService.downloadCertificados69(declaracion.id, idCompany, rut, token);
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'No se generó el certificado', text: error.message });
        } finally {
            setOcupado(false);
        }
    };

    const filaLinea = (linea) => {
        const campo = `cod_${linea.codigo}`;
        const nota = notaDeLinea(linea.codigo, propuesta);
        return (
            <tr key={linea.codigo}>
                <td className="dj1947-codigo">{linea.codigo}</td>
                <td>
                    {linea.titulo}
                    {nota && <span className="dj1947-nota">{nota}</span>}
                </td>
                <td className="text-right">{propuesta[campo] !== undefined ? pesos(propuesta[campo]) : '—'}</td>
                <td className="text-right">
                    {linea.calculada
                        ? pesos(valorLinea(formulario.base, linea.codigo))
                        : <CampoMonto valor={formulario.base[campo]} largo={15} onCambiar={(valor) => cambiarBase(campo, valor)} />}
                </td>
            </tr>
        );
    };

    const filaMonto = (campo, titulo, detalle = null) => (
        <tr key={campo}>
            <td>
                {titulo}
                {detalle && <span className="dj1947-nota">{detalle}</span>}
            </td>
            <td className="text-right">{propuesta[campo] !== undefined ? pesos(propuesta[campo]) : '—'}</td>
            <td className="text-right">
                <CampoMonto valor={formulario.base[campo]} largo={15} onCambiar={(valor) => cambiarBase(campo, valor)} />
            </td>
        </tr>
    );

    const { totales: t, filas, totalesDj, errores, avisos } = vista;
    // El CSV y los certificados salen de lo guardado: sin cambios pendientes ni errores
    const puedeDescargar = !ocupado && !hayCambios && errores.length === 0;

    return (
        <div className="dj1947-tab">
            <div className="dj1947-header">
                <h3>DJ 1947 - Base imponible, créditos y PPM de los propietarios - AT {year} (año comercial {anioComercial})</h3>
                <p className="dj1947-subtitle">
                    Régimen Pro Pyme Transparente (14 D N°8). TAXI propone la base imponible con sus datos del año comercial {anioComercial}:
                    revisa cada línea y corrige lo que haga falta. La renta 14 D N°8 usará lo que guardes aquí.
                </p>
            </div>

            <div className="dj1947-section">
                <h4>Base imponible del año (recuadro 22 del F22)</h4>
                <div className="dj1947-table-wrap">
                    <table className="dj1947-table">
                        <thead>
                            <tr>
                                <th>Código</th>
                                <th>Concepto</th>
                                <th className="text-right">Propuesto por TAXI</th>
                                <th className="text-right">Monto a declarar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {LINEAS_INGRESOS.map(filaLinea)}
                            <tr className="dj1947-subtotal">
                                <td className="dj1947-codigo">1610</td>
                                <td>Total de ingresos anuales</td>
                                <td />
                                <td className="text-right">{pesos(t.ingresos)}</td>
                            </tr>
                            {LINEAS_EGRESOS.map(filaLinea)}
                            <tr className="dj1947-subtotal">
                                <td className="dj1947-codigo">1629</td>
                                <td>Total de egresos anuales</td>
                                <td />
                                <td className="text-right">{pesos(t.egresos)}</td>
                            </tr>
                            <tr className="dj1947-total">
                                <td className="dj1947-codigo">1630</td>
                                <td>Base imponible a asignar a propietarios que son contribuyentes de impuestos finales, o pérdida tributaria del ejercicio</td>
                                <td />
                                <td className="text-right">{pesos(t.baseImponible)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="dj1947-section">
                <h4>PPM y créditos que se reparten</h4>
                <div className="dj1947-table-wrap">
                    <table className="dj1947-table">
                        <thead>
                            <tr>
                                <th>Concepto</th>
                                <th className="text-right">Propuesto por TAXI</th>
                                <th className="text-right">Monto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filaMonto('ppm_historico', 'PPM pagados en el año')}
                            {filaMonto('ppm_reajuste', 'Reajuste de los PPM')}
                            <tr className="dj1947-subtotal">
                                <td>PPM puestos a disposición de los propietarios (C14)</td>
                                <td />
                                <td className="text-right">{pesos(t.ppmActualizado)}</td>
                            </tr>
                            {CREDITOS_IDPC.map((credito) => filaMonto(credito.campo, `Crédito por IDPC (${credito.columna}): ${credito.titulo.toLowerCase()}`,
                                credito.columna === 'C6' ? 'Por los dividendos o retiros percibidos de otras empresas. Su suma es el 1605.' : null))}
                            {filaMonto('credito_ing_dif_no_sujeto', 'Crédito por ingreso diferido no sujeto a restitución (C11)')}
                            {filaMonto('credito_ing_dif_sujeto', 'Crédito por ingreso diferido sujeto a restitución (C12)',
                                `Ingresa el monto total: la DJ informa el ${Math.round(CASTIGO_SUJETO_RESTITUCION * 100)} %, ${pesos(Math.round((Number(formulario.base.credito_ing_dif_sujeto) || 0) * CASTIGO_SUJETO_RESTITUCION))}.`)}
                        </tbody>
                    </table>
                </div>
                <p className="dj1947-ayuda">El crédito del art. 33 bis (C13) y el ingreso diferido (C4) salen de los códigos 1609 y 1608 del recuadro 22.</p>
            </div>

            <div className="dj1947-section">
                <h4>Propietarios</h4>
                <div className="dj1947-table-wrap">
                    <table className="dj1947-table dj1947-table--propietarios">
                        <thead>
                            <tr>
                                <th>RUT</th>
                                <th>Nombre</th>
                                <th className="text-right">% utilidades</th>
                                <th className="text-right">Retiros del año</th>
                                <th className="text-right">N° certificado</th>
                                <th className="text-right">Base imponible</th>
                                <th className="text-right">Créditos IDPC</th>
                                <th className="text-right">Crédito 33 bis</th>
                                <th className="text-right">PPM</th>
                            </tr>
                        </thead>
                        <tbody>
                            {formulario.propietarios.map((p, i) => {
                                const fila = filas[i];
                                const creditosIdpc = CREDITOS_IDPC.reduce((total, credito) => total + fila[credito.campo], 0);
                                return (
                                    <tr key={p.id}>
                                        <td className="dj1947-rut">{p.rut}</td>
                                        <td>{p.nombre}</td>
                                        <td className="text-right">
                                            <input
                                                className="dj1947-porcentaje"
                                                value={p.porcentaje}
                                                inputMode="decimal"
                                                onChange={(e) => {
                                                    if (/^\d{0,3}([.,]\d{0,4})?$/.test(e.target.value)) cambiarPropietario(p.id, 'porcentaje', e.target.value);
                                                }}
                                            />
                                        </td>
                                        <td className="text-right">
                                            <CampoMonto valor={p.retiros} largo={15} onCambiar={(valor) => cambiarPropietario(p.id, 'retiros', valor)} />
                                        </td>
                                        <td className="text-right">
                                            <button
                                                className="dj1947-btn-cert"
                                                onClick={() => handleCertificados(p.rut)}
                                                disabled={!puedeDescargar}
                                                title="Descargar el Certificado N° 69 de este propietario"
                                            >
                                                <FaFilePdf /> N° {fila.numero_certificado}
                                            </button>
                                        </td>
                                        <td className="text-right">{pesos(fila.base_imponible)}</td>
                                        <td className="text-right">{pesos(creditosIdpc)}</td>
                                        <td className="text-right">{pesos(fila.credito_33_bis)}</td>
                                        <td className="text-right">{pesos(fila.ppm)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {filas.length > 0 && (
                            <tfoot>
                                <tr>
                                    <td colSpan={2}>Totales ({totalesDj.casos} propietarios)</td>
                                    <td className="text-right">
                                        {formulario.propietarios.reduce((total, p) => total + aNumeroPorcentaje(p.porcentaje), 0).toLocaleString('es-CL', { maximumFractionDigits: 4 })} %
                                    </td>
                                    <td className="text-right">{pesos(totalesDj.retiros)}</td>
                                    <td />
                                    <td className="text-right">{pesos(totalesDj.base_imponible)}</td>
                                    <td className="text-right">{pesos(CREDITOS_IDPC.reduce((total, credito) => total + totalesDj[credito.campo], 0))}</td>
                                    <td className="text-right">{pesos(totalesDj.credito_33_bis)}</td>
                                    <td className="text-right">{pesos(totalesDj.ppm)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
                <p className="dj1947-ayuda">
                    Los socios y sus retiros vienen de Socios y dividendos. La base, los créditos y los PPM se reparten según el porcentaje de participación
                    en las utilidades; la diferencia del redondeo queda en el socio con mayor porcentaje. Los certificados se numeran solos, desde 1, en
                    el orden de esta tabla. El Certificado N° 69 de cada propietario se emite hasta el 30 de marzo (Res. Ex. SII N° 97 de 2020).
                </p>
            </div>

            {(errores.length > 0 || avisos.length > 0) && (
                <div className="dj1947-revision">
                    {errores.map((error) => <p key={error} className="dj1947-error"><FaTimesCircle /> {error}</p>)}
                    {avisos.map((aviso) => <p key={aviso} className="dj1947-aviso"><FaExclamationTriangle /> {aviso}</p>)}
                </div>
            )}

            <div className="dj1947-actions">
                <button className="dj1947-btn-secundario" onClick={handleRegenerar} disabled={ocupado}>
                    <FaSyncAlt /> Volver a traer de TAXI
                </button>
                <button className="dj1947-btn-secundario" onClick={handleGuardar} disabled={ocupado || !hayCambios || errores.length > 0}>
                    <FaSave /> Guardar
                </button>
                <button className="dj1947-btn-csv" onClick={handleDescargar} disabled={ocupado || hayCambios || errores.length > 0}>
                    <FaFileDownload /> Descargar CSV DJ1947
                </button>
                <button className="dj1947-btn-secundario" onClick={() => handleCertificados()} disabled={!puedeDescargar}>
                    <FaFilePdf /> Descargar certificados N° 69
                </button>
                {hayCambios && <p className="dj1947-warning">Hay cambios sin guardar: guárdalos antes de descargar el CSV o los certificados.</p>}
            </div>
        </div>
    );
};

export default DJ1947TransparenciaTab;
